import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import {
  Button,
  ListingCard,
  PaginationLinks,
  VerifiedCreatorCardSkeleton,
} from '../../../components';

import css from './SearchResultsPanel.module.css';

const SKELETON_CARD_COUNT = 8;

const ListingCardSkeleton = ({ index }) => (
  <li
    className={css.resultItem}
    aria-hidden="true"
    data-testid="search-listing-card-skeleton"
    key={`listing-card-skeleton-${index}`}
  >
    <VerifiedCreatorCardSkeleton />
  </li>
);

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {Array<Object>} props.sellerCards - Seller card data for seller search mode
 * @param {Function} props.onLoadMoreSellers - The function to fetch the next sellers page
 * @param {boolean} [props.showLoadingSkeletons] - Whether to show first-page loading skeletons
 * @param {boolean} [props.loadMoreInProgress] - Whether the next page is loading
 * @param {Object} [props.loadMoreSellersError] - Load more error
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    sellerCards = [],
    pagination,
    search,
    setActiveListing,
    onLoadMoreSellers,
    showLoadingSkeletons = false,
    loadMoreInProgress = false,
    loadMoreSellersError,
    isMapVariant = true,
    listingTypeParam,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const isSellerMode = sellerCards.length > 0 || !!onLoadMoreSellers;
  const showSkeletons = showLoadingSkeletons || loadMoreInProgress;
  const hasNextPage = pagination && pagination.page < pagination.totalPages;
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';
  const loadMoreButton =
    isSellerMode && hasNextPage && onLoadMoreSellers ? (
      <div className={css.loadMoreWrapper}>
        <Button
          type="button"
          className={css.loadMoreButton}
          onClick={onLoadMoreSellers}
          disabled={loadMoreInProgress}
          aria-label={intl.formatMessage({ id: 'SearchResultsPanel.loadMoreSellers' })}
        >
          <FormattedMessage id="SearchResultsPanel.loadMoreSellers" />
        </Button>
        {loadMoreSellersError ? (
          <p className={css.loadMoreError}>
            <FormattedMessage id="SearchResultsPanel.loadMoreSellersError" />
          </p>
        ) : null}
      </div>
    ) : null;
  const paginationLinks =
    !isSellerMode && pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {isSellerMode
          ? sellerCards.map(seller => (
              <li key={seller.id} className={css.resultItem}>
                <ListingCard
                  className={css.listingCard}
                  variant="verifiedCreatorCollection"
                  thumbnailImage={seller.profileImage}
                  thumbnailAlt={seller.name}
                  itemsCountText={seller.itemsCountText}
                  categoryLabel={seller.category}
                  creatorName={seller.name}
                  creatorSubtitle={seller.subtitle}
                  ctaText={intl.formatMessage({ id: 'SearchPage.sellerCardCta' })}
                  ctaHref={seller.profileHref}
                  intl={intl}
                />
              </li>
            ))
          : listings.map(l => (
              <li key={l.id.uuid} className={css.resultItem}>
                <ListingCard
                  className={css.listingCard}
                  listing={l}
                  renderSizes={cardRenderSizes(isMapVariant)}
                  setActiveListing={setActiveListing}
                />
              </li>
            ))}
        {showSkeletons
          ? Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
              <ListingCardSkeleton index={index} key={`listing-card-skeleton-${index}`} />
            ))
          : null}
        {props.children}
      </ul>
      <span className={css.loadingMoreStatus} role="status" aria-live="polite">
        {loadMoreInProgress ? (
          <FormattedMessage id="SearchResultsPanel.loadingMoreSellers" />
        ) : null}
      </span>
      {loadMoreButton}
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
